import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, UploadCloud, Handshake } from "lucide-react";
import {
  adminFetchCompanies,
  adminCreateCompany,
  adminPatchCompany,
  adminDeleteCompany,
  API,
} from "@/lib/api";
import { adminInputCls, adminBtnCls, adminGoldBtnCls, adminTabCls } from "./shared";

const TYPES = ["builder", "gc", "remodeling", "construction", "developer", "other"];

interface Company {
  id: string;
  name: string;
  type: string;
  type_label: string;
  logo: string;
  order: number;
  published: boolean;
}

export const CompaniesTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [form, setForm] = useState({ name: "", type: "builder", published: true });
  const [logo, setLogo] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);

  const load = useCallback(() => {
    adminFetchCompanies()
      .then((r) => {
        setCompanies(r.data.companies);
        setOrderDirty(false);
      })
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setCompanies([]);
      });
  }, [onAuthError]);

  useEffect(() => load(), [load]);

  const patch = async (id: string, payload: Record<string, unknown>) => {
    try {
      await adminPatchCompany(id, payload);
      load();
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("type", form.type);
      fd.append("published", String(form.published));
      if (logo) fd.append("logo", logo);
      await adminCreateCompany(fd);
      toast.success("Company added");
      setForm({ name: "", type: "builder", published: true });
      setLogo(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Could not add company");
    }
    setSaving(false);
  };

  const move = (idx: number, dir: -1 | 1) => {
    if (!companies) return;
    const target = idx + dir;
    if (target < 0 || target >= companies.length) return;
    const copy = [...companies];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setCompanies(copy);
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    if (!companies) return;
    setSaving(true);
    try {
      await Promise.all(companies.map((c, i) => adminPatchCompany(c.id, { order: i })));
      toast.success("Order saved");
      setOrderDirty(false);
      load();
    } catch {
      toast.error("Could not save order");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-companies-tab">
      <div className="flex items-start gap-3 border border-white/10 bg-charcoal p-6">
        <Handshake className="mt-1 h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
        <p className="text-sm leading-relaxed text-neutral-400">
          Companies shown in the public <span className="font-bold text-white">Companies We Work With</span> section.
          Only upload a logo when you have permission to use it. Never add quotes, ratings, or
          endorsements from these companies.
        </p>
      </div>

      <form
        data-testid="company-form"
        onSubmit={create}
        className="mt-6 grid gap-4 border border-white/10 bg-charcoal p-6 sm:grid-cols-2"
      >
        <input
          data-testid="company-name-input"
          required
          placeholder="Company name *"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className={adminInputCls}
        />
        <select
          data-testid="company-type-select"
          value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          className={adminInputCls}
        >
          {TYPES.map((ty) => (
            <option key={ty} value={ty}>{ty}</option>
          ))}
        </select>
        <label
          data-testid="company-logo-zone"
          className="flex cursor-pointer items-center gap-2 border border-dashed border-white/20 bg-ink px-4 py-3 text-xs text-neutral-400 transition-colors duration-300 hover:border-gold/50"
        >
          <UploadCloud className="h-4 w-4 shrink-0 text-gold" />
          {logo ? logo.name : "Upload logo (optional, with permission)"}
          <input
            data-testid="company-logo-input"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            className="sr-only"
            onChange={(e) => setLogo(e.target.files?.[0] || null)}
          />
        </label>
        <div className="flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-300">
            <input
              data-testid="company-published-checkbox"
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              className="h-4 w-4"
            />
            Published
          </label>
          <button data-testid="company-create-btn" type="submit" disabled={saving} className={adminGoldBtnCls}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add Company
          </button>
        </div>
      </form>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-neutral-400">{companies?.length ?? 0} companies</p>
        {orderDirty && (
          <button data-testid="companies-save-order-btn" onClick={saveOrder} disabled={saving} className={adminGoldBtnCls}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Order
          </button>
        )}
      </div>

      <ul className="mt-4 space-y-3" data-testid="companies-list">
        {companies === null ? (
          <li className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-gold" />
          </li>
        ) : (
          companies.map((c, i) => (
            <li
              key={c.id}
              data-testid={`company-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-charcoal px-5 py-4"
            >
              <span className="flex items-center gap-4">
                {c.logo ? (
                  <img src={`${API}/companies/logo/${c.logo}`} alt="" className="h-8 w-auto object-contain" />
                ) : null}
                <span>
                  <span className="block text-sm font-bold text-white">{c.name}</span>
                  <span className="mt-0.5 block text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                    {c.type_label}
                  </span>
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <select
                  data-testid={`company-type-${c.id}`}
                  value={c.type}
                  onChange={(e) => patch(c.id, { type: e.target.value })}
                  className={`${adminInputCls} w-auto py-2 text-xs`}
                >
                  {TYPES.map((ty) => (
                    <option key={ty} value={ty}>{ty}</option>
                  ))}
                </select>
                <button
                  data-testid={`company-publish-${c.id}`}
                  onClick={() => patch(c.id, { published: !c.published })}
                  className={adminTabCls(c.published)}
                >
                  {c.published ? <Eye className="mr-1 inline h-3.5 w-3.5" /> : <EyeOff className="mr-1 inline h-3.5 w-3.5" />}
                  {c.published ? "Published" : "Hidden"}
                </button>
                <button data-testid={`company-up-${c.id}`} onClick={() => move(i, -1)} className="p-2 text-neutral-500 transition-colors duration-200 hover:text-gold" aria-label="Move up">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button data-testid={`company-down-${c.id}`} onClick={() => move(i, 1)} className="p-2 text-neutral-500 transition-colors duration-200 hover:text-gold" aria-label="Move down">
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  data-testid={`company-delete-${c.id}`}
                  onClick={() => {
                    if (window.confirm(`Remove ${c.name}?`))
                      adminDeleteCompany(c.id).then(load).catch(() => toast.error("Delete failed"));
                  }}
                  className="p-2 text-neutral-500 transition-colors duration-200 hover:text-red-400"
                  aria-label={`Remove ${c.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};
